import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import GlovoLogo from '@assets/images/glovo.svg?react'
import UberEatsLogo from '@assets/images/uberEats.svg?react'
import { useCurrentAccount } from '@/features/auth/hooks/useCurrentAccount'
import { useAuth } from '@/features/auth/context/authProvider'
import Alert from '@/shared/components/Alert/Alert'
import Button from '@/shared/components/Button/Button'
import Chip from '@/shared/components/Chip/Chip'
import ConfirmationModal from '@/shared/components/Modal/ConfirmationModal/ConfirmationModal'
import Select from '@/shared/components/Select/Select'
import Skeleton from '@/shared/components/Skeleton/Skeleton'
import TextField from '@/shared/components/TextField/TextField'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { selectValueType } from '@/shared/components/DynamicForm/DynamicForm'
import { OptionType } from '@/shared/api/BaseAPI.model'
import SettingsPanel from '@/features/settings/components/SettingsPanel'
import { DeliveryConnectionsContext } from './api'
import { deliveryConnectionsKey } from './types'
import type { AcceptanceMode, DeliveryProvider } from './types'
import styles from './DeliveryConnections.module.scss'

/** What the backend accepts for a provider store id (UpsertGlovoIntegrationDto). */
const STORE_ID_MAX_LENGTH = 255

const PROVIDER_LABEL: Record<DeliveryProvider, string> = {
  'uber-eats': 'Uber Eats',
  glovo: 'Glovo'
}

// Inline, not <img>: the Uber wordmark takes the text colour, as Uber's own
// mark does on a dark surface.
const PROVIDER_LOGO: Record<
  DeliveryProvider,
  { Mark: typeof GlovoLogo; className: string }
> = {
  'uber-eats': { Mark: UberEatsLogo, className: styles.uberEats },
  glovo: { Mark: GlovoLogo, className: styles.glovo }
}

/*
  The tab and each provider's section read the same queries: the tab decides
  its one notice from them, the section shows and changes them. React Query
  shares the request between the two.

  Read afresh each time the tab opens, against the app's default of never
  refetching on mount: a store the MENU! team connects meanwhile has to show
  up without a reload, and there is no Refresh button to ask for it.
*/
function useConnection(restaurantId: string, provider: DeliveryProvider) {
  const { currentUser } = useAuth()
  const client = useContext(DeliveryConnectionsContext)
  const key = deliveryConnectionsKey(currentUser?.uid, restaurantId, provider)
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => client.get(restaurantId, provider, signal),
    enabled: !!restaurantId,
    refetchOnMount: 'always',
    retry: false
  })
  return { key, query }
}

function useUberCapabilities(restaurantId: string) {
  const { currentUser } = useAuth()
  const client = useContext(DeliveryConnectionsContext)
  return useQuery({
    queryKey: [
      ...deliveryConnectionsKey(currentUser?.uid, restaurantId, 'uber-eats'),
      'capabilities'
    ],
    queryFn: () => client.capabilities(restaurantId),
    enabled: !!restaurantId,
    refetchOnMount: 'always',
    retry: false
  })
}

/*
  Never read at all. A refetch that fails after a good read keeps that read
  (React Query holds on to `data`), and the tab goes on showing it.
*/
const isUnread = (query: { isError: boolean; data: unknown }) =>
  query.isError && query.data === undefined

type ActionKind =
  'start' | 'activate' | 'verify' | 'disconnect' | 'draft' | 'mode' | 'cancel'

interface SectionProps {
  restaurantId: string
  restaurantName: string
  provider: DeliveryProvider
}

function ProviderSection({
  restaurantId,
  restaurantName,
  provider
}: SectionProps) {
  const { t } = useTranslation()
  const { showSnackbar, closeSnackbar } = useSnackbar()
  const client = useContext(DeliveryConnectionsContext)
  const queryClient = useQueryClient()
  const { key, query } = useConnection(restaurantId, provider)
  const capabilities = useUberCapabilities(restaurantId)
  const isUber = provider === 'uber-eats'
  const label = PROVIDER_LABEL[provider]
  const logo = PROVIDER_LOGO[provider]
  const connection = query.data

  const [sessionId, setSessionId] = useState<string | null>(null)
  const [storeId, setStoreId] = useState('')
  const [draftId, setDraftId] = useState('')
  // The mode sent with a new activation; a connected store's own mode is
  // shown straight from the server, so the two can never drift apart.
  const [activationMode, setActivationMode] = useState<AcceptanceMode>('MANUAL')
  const [confirm, setConfirm] = useState<
    'activate' | 'disconnect' | 'automatic' | null
  >(null)
  const popup = useRef<Window | null>(null)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      popup.current?.close()
    }
  }, [])

  const savedMode = connection?.acceptanceMode
  // A Glovo store id saved before is what the field starts from.
  const savedStoreId = connection?.externalStoreId
  useEffect(() => {
    if (!isUber && savedStoreId) setDraftId(savedStoreId)
  }, [isUber, savedStoreId])

  const session = useQuery({
    queryKey: [...key, 'session', sessionId],
    queryFn: ({ signal }) => client.session(restaurantId, sessionId!, signal),
    enabled: !!sessionId,
    retry: false,
    refetchInterval: q =>
      q.state.status === 'error' ||
      (q.state.data !== undefined && q.state.data.status !== 'waiting')
        ? false
        : 2000
  })

  // Outcomes go to the app's banners, so the tab keeps a single notice.
  const tell = (message: string, severity: 'success' | 'info' | 'error') =>
    showSnackbar(label, t(`DELIVERY.${message}`), severity)

  const action = useMutation({
    mutationFn: async ({
      kind,
      nextMode
    }: {
      kind: ActionKind
      nextMode?: AcceptanceMode
    }) => {
      if (kind === 'cancel') {
        await client.cancel(restaurantId, sessionId!)
        setSessionId(null)
        popup.current?.close()
        return
      }
      if (kind === 'start') {
        const result = await client.start(restaurantId)
        if (!mounted.current) {
          popup.current?.close()
          return
        }
        setSessionId(result.sessionId)
        setStoreId('')
        setActivationMode(savedMode ?? 'MANUAL')
        if (popup.current) {
          popup.current.opener = null
          popup.current.location.href = result.authorizationUrl
        }
        return
      }
      if (kind === 'draft') {
        await client.save(restaurantId, 'glovo', {
          externalStoreId: draftId.trim(),
          enabled: false,
          acceptanceMode: 'AUTOMATIC'
        })
        tell('DRAFT_SAVED', 'success')
      } else if (kind === 'mode' && nextMode) {
        /*
          Only the mode, through the endpoint that changes only the mode.
          Sending the whole connection back would carry this panel's copy of
          `enabled` with it, and that copy goes stale the moment anyone else
          connects or disconnects the venue.
        */
        await client.setAcceptanceMode(restaurantId, nextMode)
        tell('SAVED', 'success')
      } else if (kind === 'disconnect') {
        if (isUber) await client.disconnect(restaurantId)
        else if (connection)
          await client.save(restaurantId, provider, {
            ...connection,
            enabled: false
          })
        tell('DISCONNECTED', 'info')
      } else {
        const result =
          kind === 'activate'
            ? await client.activate(
                restaurantId,
                sessionId!,
                storeId,
                activationMode
              )
            : await client.verify(restaurantId)
        if (result.active) tell('VERIFIED', 'success')
        else tell(result.pending ? 'PENDING' : 'INACTIVE', 'info')
        if (kind === 'activate') {
          setSessionId(null)
          popup.current?.close()
        }
      }
      await queryClient.invalidateQueries({ queryKey: key })
    },
    onError: error => {
      const failCode = (error as { response?: { status?: number } })?.response
        ?.status
      const text =
        ({ 403: 'NO_PERMISSION', 409: 'CONFLICT' } as Record<number, string>)[
          failCode ?? 0
        ] ?? 'REQUEST_FAILED'
      tell(text, 'error')
    }
  })
  const busy = action.isPending
  let stateKey = 'NOT_CONNECTED'
  if (connection) stateKey = connection.enabled ? 'ENABLED' : 'DISABLED'

  /*
    The Uber login runs in its own window. While it does, a banner that stays
    up says so; how it ended replaces it.
  */
  const sessionStatus = session.isError ? 'failed' : session.data?.status
  const stores = session.data?.stores ?? []
  useEffect(() => {
    if (!sessionId) return undefined
    if (sessionStatus === 'waiting') {
      const banner = showSnackbar(label, t('DELIVERY.WAITING_AUTH'), 'info', {
        persist: true
      })
      return () => closeSnackbar(banner)
    }
    if (sessionStatus === 'failed') {
      tell('AUTH_FAILED', 'error')
      // Closed on the server too where it is still open there, as Cancel
      // would; a failed poll is not proof that it has ended.
      client.cancel(restaurantId, sessionId).catch(() => undefined)
      setSessionId(null)
      popup.current?.close()
    } else if (sessionStatus === 'ready' && stores.length === 0) {
      tell('NO_STORES', 'info')
      action.mutate({ kind: 'cancel' })
    }
    return undefined
    // Keyed on where the login got to: each step is told once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, sessionStatus])

  const modeOptions: OptionType[] = useMemo(
    () => [
      { value: 'MANUAL', label: t('DELIVERY.MANUAL') },
      { value: 'AUTOMATIC', label: t('DELIVERY.AUTOMATIC') }
    ],
    [t]
  )
  const storeOptions: OptionType[] = stores.map(store => ({
    value: store.id,
    label: `${store.name}, ${store.address}`
  }))
  const pickOne = (next: selectValueType): string | null =>
    !next || Array.isArray(next) ? null : next.value

  const isPicking =
    !!sessionId && sessionStatus === 'ready' && stores.length > 0
  const canConnect =
    isUber &&
    !isUnread(query) &&
    !connection?.enabled &&
    !!capabilities.data?.oauthAvailable &&
    !sessionId

  const modeSelect = (
    value: AcceptanceMode,
    onPick: (next: AcceptanceMode) => void
  ) => (
    <Select
      label={t('DELIVERY.ACCEPTANCE')}
      options={modeOptions}
      value={modeOptions.find(option => option.value === value) ?? null}
      disabled={busy}
      onChange={next => {
        const picked = pickOne(next) as AcceptanceMode | null
        if (picked && picked !== value) onPick(picked)
      }}
      focused
      hideClearIcon
      readonly
    />
  )

  let confirmTitle = t(isUber ? 'DELIVERY.DISCONNECT' : 'DELIVERY.PAUSE')
  let confirmMessage = t(
    isUber ? 'DELIVERY.CONFIRM_DISCONNECT' : 'DELIVERY.CONFIRM_PAUSE',
    { name: restaurantName }
  )
  if (confirm === 'activate') {
    confirmTitle = t('DELIVERY.ACTIVATE')
    confirmMessage = t('DELIVERY.CONFIRM_ACTIVATE', { name: restaurantName })
    // Activating straight into automatic acceptance warns as switching does.
    if (activationMode === 'AUTOMATIC')
      confirmMessage = `${confirmMessage} ${t('DELIVERY.CONFIRM_AUTOMATIC')}`
  }
  if (confirm === 'automatic') {
    confirmTitle = t('DELIVERY.AUTOMATIC')
    confirmMessage = t('DELIVERY.CONFIRM_AUTOMATIC')
  }

  return (
    <section className={styles.section} aria-label={label}>
      <div className={styles.header}>
        <h3 className={styles.logo}>
          <logo.Mark
            className={logo.className}
            role="img"
            aria-label={label}
          />
        </h3>
        {/* Once read: a tag that changes as it loads reads as a fault. */}
        {query.isSuccess && (
          <Chip label={t(`DELIVERY.${stateKey}`)} color="primary" />
        )}
      </div>

      {query.isPending && <Skeleton width="12rem" height="2.5rem" />}

      {/* Glovo's store is typed in until it is live; then it only shows. */}
      {connection && (isUber || connection.enabled) && (
        <div className={styles.readOnly}>
          <p className={`label-bold-small ${styles.readOnlyLabel}`}>
            {t('DELIVERY.STORE_ID')}
          </p>
          <span className="body-bold-medium">{connection.externalStoreId}</span>
        </div>
      )}
      {!isUber &&
        !query.isPending &&
        !isUnread(query) &&
        !connection?.enabled && (
          <TextField
            label={t('DELIVERY.STORE_ID')}
            value={draftId}
            disabled={busy}
            onChange={event =>
              setDraftId(event.target.value.slice(0, STORE_ID_MAX_LENGTH))
            }
          />
        )}

      {/*
        On a connected store the mode applies as it is picked; automatic
        acceptance asks first, since no one confirms the orders after it.
      */}
      {isUber &&
        connection &&
        !sessionId &&
        modeSelect(savedMode ?? 'MANUAL', next =>
          next === 'AUTOMATIC'
            ? setConfirm('automatic')
            : action.mutate({ kind: 'mode', nextMode: next })
        )}

      {isPicking && (
        <>
          <Select
            label={t('DELIVERY.SELECT_STORE')}
            options={storeOptions}
            value={
              storeOptions.find(option => option.value === storeId) ?? null
            }
            disabled={busy}
            onChange={next => setStoreId(pickOne(next) ?? '')}
            focused
            hideClearIcon
            readonly
          />
          {/* Sent along with the store when it is activated. */}
          {modeSelect(activationMode, setActivationMode)}
        </>
      )}

      <div className={styles.actions}>
        {canConnect && (
          <Button
            label={t('DELIVERY.CONNECT_UBER')}
            variant="text"
            color="primary"
            icon="las la-link icon-medium"
            disabled={busy || query.isPending}
            onClick={() => {
              popup.current?.close()
              popup.current = window.open(
                'about:blank',
                '_blank',
                'popup,width=600,height=760'
              )
              if (!popup.current) {
                tell('POPUP_BLOCKED', 'error')
                return
              }
              popup.current.opener = null
              action.mutate(
                { kind: 'start' },
                { onError: () => popup.current?.close() }
              )
            }}
          />
        )}
        {isPicking && (
          <Button
            label={t('DELIVERY.ACTIVATE')}
            variant="text"
            color="primary"
            icon="las la-check icon-medium"
            disabled={busy || !storeId}
            onClick={() => setConfirm('activate')}
          />
        )}
        {sessionId && (
          <Button
            label={t('COMMON.CANCEL')}
            variant="text"
            color="primary"
            disabled={busy}
            onClick={() => action.mutate({ kind: 'cancel' })}
          />
        )}
        {isUber && connection && !sessionId && (
          <Button
            label={t('DELIVERY.VERIFY')}
            variant="text"
            color="primary"
            icon="las la-sync-alt icon-medium"
            disabled={busy}
            onClick={() => action.mutate({ kind: 'verify' })}
          />
        )}
        {connection && !sessionId && (isUber || connection.enabled) && (
          <Button
            label={t(isUber ? 'DELIVERY.DISCONNECT' : 'DELIVERY.PAUSE')}
            variant="text"
            color="primary"
            icon={
              isUber ? 'las la-unlink icon-medium' : 'las la-pause icon-medium'
            }
            disabled={busy}
            onClick={() => setConfirm('disconnect')}
          />
        )}
        {!isUber &&
          !query.isPending &&
          !isUnread(query) &&
          !connection?.enabled && (
            <Button
              label={t('COMMON.SAVE')}
              variant="text"
              color="primary"
              disabled={
                busy || !draftId.trim() || draftId.trim() === savedStoreId
              }
              onClick={() => action.mutate({ kind: 'draft' })}
            />
          )}
      </div>

      <ConfirmationModal
        isOpen={confirm !== null}
        title={confirmTitle}
        message={confirmMessage}
        resetsFormDirty={false}
        // The mode and an activation can be changed back here; undoing a
        // disconnect or a pause takes a new login or the MENU! team.
        irreversible={confirm === 'disconnect'}
        // Opened from inside the settings modal, whose centring transform would
        // otherwise trap it in that box (Modal.tsx).
        disablePortal={false}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm === 'automatic')
            action.mutate({ kind: 'mode', nextMode: 'AUTOMATIC' })
          else
            action.mutate({
              kind: confirm === 'activate' ? 'activate' : 'disconnect'
            })
          setConfirm(null)
        }}
      />
    </section>
  )
}

/*
  The tab's one notice: a read that failed, or which channels the MENU! team
  still has to set up. Everything that happens after a click is a banner.
*/
function useTabNotice(restaurantId: string) {
  const { t } = useTranslation()
  const uber = useConnection(restaurantId, 'uber-eats').query
  const glovo = useConnection(restaurantId, 'glovo').query
  const capabilities = useUberCapabilities(restaurantId)

  if (isUnread(uber) || isUnread(glovo))
    return {
      severity: 'error' as const,
      message: t('DELIVERY.LOAD_FAILED'),
      retry: () => {
        uber.refetch().catch(() => undefined)
        glovo.refetch().catch(() => undefined)
        capabilities.refetch().catch(() => undefined)
      }
    }
  /*
    Whether Uber's own login is open is asked of a route that needs more
    rights than reading the connections. A refusal there means the same as
    "not open": the team connects it, and the rest of the tab still shows.
  */
  const uberByTeam =
    (capabilities.isError ||
      (capabilities.isSuccess && !capabilities.data.oauthAvailable)) &&
    uber.isSuccess &&
    !uber.data?.enabled
  // Glovo has no self-service connection at all yet.
  const glovoByTeam = glovo.isSuccess && !glovo.data?.enabled
  if (uberByTeam && glovoByTeam)
    return { severity: 'info' as const, message: t('DELIVERY.TEAM_SETUP_ALL') }
  if (uberByTeam || glovoByTeam)
    return {
      severity: 'info' as const,
      message: t('DELIVERY.TEAM_SETUP_ONE', {
        channel: PROVIDER_LABEL[uberByTeam ? 'uber-eats' : 'glovo']
      })
    }
  return null
}

export default function DeliveryConnections() {
  const { t } = useTranslation()
  const { restaurantId, restaurants, isLoading } = useCurrentAccount()
  // Freeze the target for this screen, even if another browser tab switches venue.
  const [targetId] = useState(restaurantId ?? '')
  const venue = restaurants.find(
    restaurant => String(restaurant.id) === targetId
  )
  const notice = useTabNotice(targetId)

  return (
    <SettingsPanel
      lead={venue ? t('DELIVERY.LEAD', { name: venue.name }) : undefined}
    >
      {!targetId ? (
        <Alert severity="error" message={t('DELIVERY.NO_VENUE')} />
      ) : (
        <div className={styles.grid}>
          {/*
            The venue list arrives separately from the id, and until it does
            there is no name to print. Falling back to the id put a raw UUID in
            the sentence asking whether to disconnect this venue.
          */}
          {isLoading || !venue ? (
            <Skeleton height="4rem" />
          ) : (
            <>
              {notice && (
                <Alert severity={notice.severity} message={notice.message} />
              )}
              {notice?.retry && (
                <div className={styles.actions}>
                  <Button
                    label={t('DELIVERY.RETRY')}
                    variant="text"
                    color="primary"
                    icon="las la-redo-alt icon-medium"
                    onClick={notice.retry}
                  />
                </div>
              )}
              {/* Nothing read is nothing to show or change: the notice says why. */}
              {notice?.severity !== 'error' &&
                (['uber-eats', 'glovo'] as const).map(provider => (
                  <ProviderSection
                    key={`${targetId}:${provider}`}
                    provider={provider}
                    restaurantId={targetId}
                    restaurantName={venue.name}
                  />
                ))}
            </>
          )}
        </div>
      )}
    </SettingsPanel>
  )
}
